import { Typography, Divider, Button, Space, Dropdown } from "antd";
import { TbCalculator, TbCashBanknote, TbDotsVertical, TbEdit, TbInfoCircle, TbRefresh } from "react-icons/tb";
import { StrategyFormat, StrategyResponse } from "dtos/strategy.dto";
import { formatMoney, toCapitalCase } from "utils/functions";
import { ItemType } from "antd/es/menu/interface";
import { SelectorKey } from "utils/core/strategy.util";

interface StrategyCardProps {
  selected?: string;
  isLoading: boolean;
  strategy: StrategyResponse;
  redirectStage: "agreements" | "ledger";
  onSelect: (props: { isSelected: boolean }) => unknown;
  onPreview?: () => unknown;
  onCustomize?: () => unknown;
  onReExecute?: () => unknown;
}

export const StrategyCard = ({
  selected,
  isLoading,
  strategy,
  redirectStage,
  onSelect,
  onPreview,
  onCustomize,
  onReExecute,
}: StrategyCardProps) => {
  const isSelected = new SelectorKey(strategy.slug).compareId(selected);
  const highlighted = selected ? isSelected : (strategy.highlighted ?? false);
  const hideSubPayment = strategy.hide?.includes("sub_payment") ?? false;

  const dropdownItems: ItemType[] = [];
  if (onPreview) {
    dropdownItems.push({
      type: "item",
      key: "preview",
      label: (
        <Space className="gap-1">
          <TbInfoCircle className="text-green-600" size={16} />
          Preview
        </Space>
      ),
      onClick: onPreview,
    });
  }

  const isCustomV1 = strategy.format === StrategyFormat.MANAGERS_APPROVAL && strategy.version === 1;
  if (onCustomize && !isCustomV1) {
    dropdownItems.push({
      type: "item",
      key: "customize",
      label: (
        <Space className="gap-1">
          <TbEdit className="text-sky-600" size={16} />
          {strategy.format === StrategyFormat.MANAGERS_APPROVAL ? "Edit" : "Customize"}
        </Space>
      ),
      onClick: onCustomize,
    });
  }

  if (isSelected && onReExecute) {
    dropdownItems.push({
      type: "item",
      key: "re_execute",
      label: (
        <Space className="gap-1">
          <TbRefresh className="text-pink-600" size={16} />
          Re-execute
        </Space>
      ),
      onClick: onReExecute,
    });
  }

  return (
    <div className="flex flex-col relative items-center w-full">
      <div className="absolute -top-3 z-10">
        <Typography.Text
          className={`px-2 text-xs text-white py-1 rounded-full ${highlighted ? "bg-purple-500" : "bg-gray-400"}`}
        >
          {strategy.overline}
        </Typography.Text>
      </div>
      <div
        className={`bg-white w-full flex flex-col p-3 aspect-[10/11] rounded-md border border-solid ${
          highlighted ? "border-purple-500 shadow-xl shadow-purple-300" : "border-gray-300 shadow-md"
        }`}
      >
        <Typography.Title level={4} className={`!text-center !mb-1 ${highlighted ? "!text-purple-400" : ""}`}>
          {strategy.title}
        </Typography.Title>
        <Typography.Text type="secondary" className="h-10 !text-center block text-balance">
          <span dangerouslySetInnerHTML={{ __html: strategy.sub_title }} />
        </Typography.Text>
        <Divider className="!my-3" />
        {hideSubPayment ? (
          <div className="h-4" />
        ) : (
          <div className="flex items-center gap-1 text-gray-500">
            <TbCalculator />
            <Typography.Text type="secondary">{strategy.sub_payment_title}</Typography.Text>
            <Typography.Text type="secondary" className="ml-auto !font-semibold">
              {formatMoney(strategy.sub_payment_amount)}
            </Typography.Text>
          </div>
        )}

        <div className="flex flex-col grow items-center">
          <div className="inline-flex gap-1 text-gray-500 justify-center items-center mt-3 mb-1">
            <TbCashBanknote />
            <Typography.Text type="secondary">{strategy.main_payment_title}</Typography.Text>
          </div>
          <Typography.Title level={2} className="!m-0">
            {formatMoney(strategy.main_payment_amount)}
          </Typography.Title>
          {strategy?.footer_text ? (
            <Typography.Text type="secondary" className="!text-xs italic block mt-3 mb-2 !line-clamp-1">
              <span dangerouslySetInnerHTML={{ __html: strategy.footer_text }} />
            </Typography.Text>
          ) : null}
          <Space.Compact className="mt-auto mb-4">
            <Button
              color={isSelected ? "primary" : "default"}
              variant="solid"
              onClick={() => onSelect({ isSelected })}
              loading={isLoading}
              disabled={strategy.unavailable}
              className="border-r-white/30"
            >
              {strategy.unavailable
                ? "Not available"
                : isSelected
                  ? `View ${toCapitalCase(redirectStage)}`
                  : "Select Plan"}
            </Button>
            <Dropdown
              overlayClassName="[&_.ant-dropdown-menu]:rounded-lg"
              menu={{
                items: dropdownItems,
              }}
            >
              <Button
                disabled={strategy.unavailable}
                color={isSelected ? "primary" : "default"}
                variant="solid"
                className="w-8 p-0 border-l-white/30"
              >
                <TbDotsVertical className="flex grow" />
              </Button>
            </Dropdown>
          </Space.Compact>
        </div>
      </div>
    </div>
  );
};
